import dayjs from "dayjs";
import * as XLSX from "xlsx";

import type { PublishedMoveLine } from "~/types/contabilidad";

interface LibroDiarioExportData {
  lines: PublishedMoveLine[];
  dateFrom: string;
  dateTo: string;
  companyName: string;
  journalName?: string;
  accountRange?: string;
}

/**
 * Exports the Libro Diario (journal) lines to Excel for the asesor fiscal.
 * Includes a filter summary header and a TOTALES row. Rows are emitted in
 * ascending chronological order (canonical libro diario presentation),
 * regardless of the on-screen sort.
 */
export const exportLibroDiarioToExcel = ({
  lines,
  dateFrom,
  dateTo,
  companyName,
  journalName,
  accountRange,
}: LibroDiarioExportData): void => {
  const workbook = XLSX.utils.book_new();
  const sheetData: (string | number)[][] = [];

  // Filter summary header
  sheetData.push(["Libro Diario"]);
  sheetData.push([`Empresa: ${companyName || "Todas las empresas"}`]);
  sheetData.push([
    `Período: ${dayjs(dateFrom).format("DD/MM/YYYY")} - ${dayjs(dateTo).format(
      "DD/MM/YYYY"
    )}`,
  ]);
  sheetData.push([`Diario: ${journalName || "Todos los diarios"}`]);
  sheetData.push([`Cuentas: ${accountRange || "Todas las cuentas"}`]);
  sheetData.push([]);

  const tableHeader = [
    "Fecha",
    "N° Asiento",
    "Diario",
    "Código Cuenta",
    "Nombre Cuenta",
    "Descripción",
    "Debe",
    "Haber",
  ];
  sheetData.push(tableHeader);

  // Ascending chronological order (oldest first) for the ledger export.
  const ordered = [...lines].sort((a, b) => {
    const dateDiff =
      new Date(a.move_date).getTime() - new Date(b.move_date).getTime();
    if (dateDiff !== 0) return dateDiff;
    return a.move_number.localeCompare(b.move_number);
  });

  let totalDebe = 0;
  let totalHaber = 0;

  ordered.forEach((line) => {
    const debit = Number(line.debit) || 0;
    const credit = Number(line.credit) || 0;
    totalDebe += debit;
    totalHaber += credit;

    sheetData.push([
      dayjs(line.move_date).format("DD/MM/YYYY"),
      line.move_number,
      line.journal_name,
      line.account_code,
      line.account_name,
      line.description || line.move_ref || "",
      debit,
      credit,
    ]);
  });

  sheetData.push([]);
  sheetData.push(["", "", "", "", "", "TOTALES", totalDebe, totalHaber]);

  const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
  worksheet["!cols"] = [
    { wch: 12 }, // Fecha
    { wch: 16 }, // N° Asiento
    { wch: 24 }, // Diario
    { wch: 14 }, // Código Cuenta
    { wch: 34 }, // Nombre Cuenta
    { wch: 42 }, // Descripción
    { wch: 16 }, // Debe
    { wch: 16 }, // Haber
  ];

  XLSX.utils.book_append_sheet(workbook, worksheet, "Libro Diario");
  XLSX.writeFile(
    workbook,
    `libro_diario_${dayjs(dateFrom).format("YYYYMMDD")}_${dayjs(dateTo).format(
      "YYYYMMDD"
    )}.xlsx`
  );
};
